import {
  PORTAL_AUTH_MESSAGE,
  PORTAL_LOGOUT_MESSAGE,
  portalPreviewOrigin,
} from "@/microfrontends/portalIframeHost";

const PORTAL_SHELL_READY_MESSAGE = "compliance-portal-shell-ready";

import { SMUser } from "@/store";

export type PortalAuthPayload = {
  session_id: string;
  expires_at: string;
  account: {
    id: string;
    name: string;
    email: string;
    role: string;
    company_id: number | null;
    legacy_user_id: number | null;
  };
};

const PORTAL_SESSION_CACHE_KEY = "nb.portal.auth";

function getPortalApiBaseUrl(): string {
  const fromEnv = process.env.VUE_APP_PORTAL_API_BASE_URL;
  const base =
    fromEnv && fromEnv.length > 0 ? fromEnv.trim() : "https://dev-api-portal.nationbetter.uk";
  return base.replace(/\/$/, "");
}

function isSessionValid(session: PortalAuthPayload): boolean {
  if (!session.expires_at) return true;
  return new Date(session.expires_at).getTime() > Date.now();
}

export function cachePortalSession(session: PortalAuthPayload): void {
  localStorage.setItem(PORTAL_SESSION_CACHE_KEY, JSON.stringify(session));
}

export function readCachedPortalSession(): PortalAuthPayload | null {
  try {
    const raw = localStorage.getItem(PORTAL_SESSION_CACHE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as PortalAuthPayload;
    if (!session?.session_id || !session?.account) return null;
    if (!isSessionValid(session)) {
      localStorage.removeItem(PORTAL_SESSION_CACHE_KEY);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(PORTAL_SESSION_CACHE_KEY);
    return null;
  }
}

export function clearCachedPortalSession(): void {
  localStorage.removeItem(PORTAL_SESSION_CACHE_KEY);
}

export async function loginPortalSession(
  email: string,
  password: string
): Promise<PortalAuthPayload | null> {
  const response = await fetch(`${getPortalApiBaseUrl()}/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      password,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.data) return null;
  return payload.data as PortalAuthPayload;
}

function warnPortalAuth(message: string, detail?: unknown): void {
  if (process.env.NODE_ENV === "production") return;
  if (detail !== undefined) {
    console.warn(`[portal-auth] ${message}`, detail);
    return;
  }
  console.warn(`[portal-auth] ${message}`);
}

async function bootstrapPortalFromLegacyToken(
  token: string,
  email?: string
): Promise<PortalAuthPayload | null> {
  const base = getPortalApiBaseUrl();
  const normalizedEmail = email?.trim().toLowerCase();
  const bodies: Record<string, string>[] = [
    { legacy_token: token },
    { token },
    { access_token: token },
    { compliance_token: token },
  ];

  if (normalizedEmail) {
    bodies.push(
      { email: normalizedEmail, legacy_token: token },
      { email: normalizedEmail, token },
      { email: normalizedEmail, password: token }
    );
  }

  for (const body of bodies) {
    const response = await fetch(`${base}/v1/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (response.ok && payload?.data) {
      return payload.data as PortalAuthPayload;
    }
  }

  const exchangeResponse = await fetch(`${base}/v1/auth/exchange`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(
      normalizedEmail
        ? { email: normalizedEmail, legacy_token: token, token }
        : { legacy_token: token, token }
    ),
  });
  const exchangePayload = await exchangeResponse.json().catch(() => ({}));
  if (exchangeResponse.ok && exchangePayload?.data) {
    return exchangePayload.data as PortalAuthPayload;
  }

  warnPortalAuth("Could not exchange compliance token for a portal session", {
    status: exchangeResponse.status,
    error: exchangePayload?.error,
  });

  return null;
}

export async function prefetchPortalSession(): Promise<PortalAuthPayload | null> {
  let session = readCachedPortalSession();
  if (session) return session;

  const legacyToken = localStorage.getItem("auth_token_default");
  if (!legacyToken) {
    warnPortalAuth("No compliance auth token; log in to compliance first");
    return null;
  }

  const email = SMUser.user?.email;
  session = await bootstrapPortalFromLegacyToken(legacyToken, email);
  if (session) {
    cachePortalSession(session);
    return session;
  }

  if (!email) {
    warnPortalAuth("Compliance user profile not loaded yet; will retry portal auth");
  }

  return null;
}

function portalMessageTarget(iframe: HTMLIFrameElement): string {
  try {
    return new URL(iframe.src).origin;
  } catch {
    return portalPreviewOrigin();
  }
}

export function postPortalAuth(iframe: HTMLIFrameElement, session: PortalAuthPayload): void {
  if (!iframe.contentWindow) return;
  iframe.contentWindow.postMessage(
    { type: PORTAL_AUTH_MESSAGE, session },
    portalMessageTarget(iframe)
  );
}

function postPortalAuthWithRetries(iframe: HTMLIFrameElement, session: PortalAuthPayload): void {
  const delays = [0, 150, 500, 1200];
  for (const delay of delays) {
    window.setTimeout(() => postPortalAuth(iframe, session), delay);
  }
}

export function postPortalLogout(iframe: HTMLIFrameElement): void {
  if (!iframe.contentWindow) return;
  iframe.contentWindow.postMessage({ type: PORTAL_LOGOUT_MESSAGE }, portalMessageTarget(iframe));
}

export function postPortalAuthToOpenIframes(session: PortalAuthPayload): void {
  document.querySelectorAll("iframe.portal-iframe").forEach((node) => {
    if (node instanceof HTMLIFrameElement) {
      postPortalAuthWithRetries(node, session);
    }
  });
}

export function postPortalLogoutToOpenIframes(): void {
  document.querySelectorAll("iframe.portal-iframe").forEach((node) => {
    if (node instanceof HTMLIFrameElement) {
      postPortalLogout(node);
    }
  });
}

export async function syncPortalAuthAfterLogin(credentials: {
  email: string;
  password: string;
}): Promise<void> {
  const session = await loginPortalSession(credentials.email, credentials.password);
  if (!session) return;

  cachePortalSession(session);
  postPortalAuthToOpenIframes(session);
}

export async function restorePortalAuthForIframe(iframe: HTMLIFrameElement): Promise<void> {
  const session = await prefetchPortalSession();

  if (session) {
    postPortalAuthWithRetries(iframe, session);
    return;
  }

  warnPortalAuth(
    "No portal session to send to iframe — re-log into compliance or check dev-api-portal"
  );
}

export function installPortalShellAuthListener(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("message", (event) => {
    if (!event?.data || typeof event.data !== "object") return;
    const message = event.data as { type?: string };
    if (message.type !== PORTAL_SHELL_READY_MESSAGE) return;

    let portalOrigin = "";
    try {
      portalOrigin = new URL(portalPreviewOrigin()).origin;
    } catch {
      return;
    }

    if (event.origin !== portalOrigin) return;

    document.querySelectorAll("iframe.portal-iframe").forEach((node) => {
      if (node instanceof HTMLIFrameElement) {
        void restorePortalAuthForIframe(node);
      }
    });
  });
}

export function clearPortalAuthInShell(): void {
  clearCachedPortalSession();
  postPortalLogoutToOpenIframes();
}
